import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMsal } from "@azure/msal-react";
import { getAuthenticatedAccount } from "../../utils/accountUtils";

const PAGE_SIZE = 10;
const ROOT_LABEL = "Unentered Documents";

const UnenteredDocumentsSelector = ({ onSelect, open, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextLink, setNextLink] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;
  const requestIdRef = useRef(0);
  const fetchDocumentsRef = useRef(null);

  const fetchDocuments = useCallback(async (folderId = null, url = null) => {
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      const response = await instance.acquireTokenSilent({
        scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
        account: account,
      });

      const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
      const siteId = process.env.REACT_APP_SP_SITE_ID;
      const unenteredDocumentsId = process.env.REACT_APP_UNENTERD_DOCUMENTS_ID;

      if (!unenteredDocumentsId) {
        throw new Error('REACT_APP_UNENTERD_DOCUMENTS_ID environment variable is not set');
      }

      const targetFolderId = folderId || unenteredDocumentsId;
      const apiUrl = url || `${graphApiUrl}/sites/${siteId}/drive/items/${targetFolderId}/children?$select=id,name,lastModifiedDateTime,webUrl,file,folder,@microsoft.graph.downloadUrl&$orderby=lastModifiedDateTime desc&$top=${PAGE_SIZE}`;

      const result = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (!result.ok) {
        const errorText = await result.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch documents: ${result.statusText}`);
      }

      const data = await result.json();

      if (!data.value) {
        throw new Error('Invalid response format from server');
      }

      setNextLink(data["@odata.nextLink"] || null);

      if (url) {
        setDocuments((prev) => {
          const seen = new Set(prev.map((item) => item.id));
          const nextItems = data.value.filter((item) => item && !seen.has(item.id));
          return [...prev, ...nextItems];
        });
      } else {
        setDocuments(data.value);
      }
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      console.error("Error fetching documents:", fetchError);
      setError(fetchError.message || 'Failed to fetch documents');
      if (!url) {
        setDocuments([]);
        setNextLink(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [instance, accounts, tenantId]);

  fetchDocumentsRef.current = fetchDocuments;

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1;
      setDocuments([]);
      setSearchQuery("");
      setNextLink(null);
      setError(null);
      setBreadcrumbs([]);
      setLoading(false);
      return;
    }

    setBreadcrumbs([]);
    setSearchQuery("");
    setError(null);
    fetchDocumentsRef.current(null);
  }, [open]);

  const handleLoadMore = () => {
    if (!nextLink) return;
    const currentFolderId = breadcrumbs.length
      ? breadcrumbs[breadcrumbs.length - 1].id
      : null;
    fetchDocuments(currentFolderId, nextLink);
  };

  const openFolder = (folder) => {
    setDocuments([]);
    setNextLink(null);
    setSearchQuery("");
    setError(null);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    fetchDocuments(folder.id);
  };

  const navigateToBreadcrumb = (index) => {
    setDocuments([]);
    setNextLink(null);
    setSearchQuery("");
    setError(null);

    if (index < 0) {
      setBreadcrumbs([]);
      fetchDocuments(null);
      return;
    }

    const nextCrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(nextCrumbs);
    fetchDocuments(nextCrumbs[index].id);
  };

  const getFileName = (item) => item.name || 'Untitled Document';

  const getFileType = (item) => {
    if (!item.file || !item.name) return 'File';
    const extension = item.name.split('.').pop().toLowerCase();
    return extension && extension !== item.name.toLowerCase() ? `.${extension}` : 'File';
  };

  const getItemLabel = (item) => {
    if (item.folder) {
      const count = item.folder.childCount;
      if (typeof count === 'number') {
        return count === 1 ? 'Folder · 1 item' : `Folder · ${count} items`;
      }
      return 'Folder';
    }
    return getFileType(item);
  };

  const filteredDocuments = (documents || []).filter((item) => {
    if (!item || (!item.file && !item.folder)) return false;
    if (!searchQuery) return true;
    return getFileName(item).toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSelect = (document) => {
    requestIdRef.current += 1;
    onSelect(document);
    onClose();
  };

  const handleItemClick = (item) => {
    if (item.folder) {
      openFolder(item);
      return;
    }
    handleSelect(item);
  };

  const emptyMessage = error
    ? 'Error loading documents'
    : searchQuery
      ? 'No matching files or folders'
      : 'This folder is empty';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle component="div">
        Select Unentered Document
        <Breadcrumbs
          maxItems={4}
          itemsBeforeCollapse={1}
          itemsAfterCollapse={2}
          aria-label="Folder path"
          sx={{ mt: 1.5 }}
        >
          {breadcrumbs.length === 0 ? (
            <Typography color="text.primary" variant="body2">
              {ROOT_LABEL}
            </Typography>
          ) : (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => navigateToBreadcrumb(-1)}
            >
              {ROOT_LABEL}
            </Link>
          )}
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast) {
              return (
                <Typography key={crumb.id} color="text.primary" variant="body2">
                  {crumb.name}
                </Typography>
              );
            }
            return (
              <Link
                key={crumb.id}
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => navigateToBreadcrumb(index)}
              >
                {crumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && documents.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {filteredDocuments.map((item) => (
              <ListItem
                key={item.id}
                disablePadding
                divider
              >
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  aria-label={item.folder ? `Open folder ${getFileName(item)}` : `Select ${getFileName(item)}`}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.folder ? (
                      <FolderIcon color="primary" />
                    ) : (
                      <InsertDriveFileOutlinedIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={getFileName(item)}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography component="span" variant="body2" color="text.secondary">
                          {getItemLabel(item)}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {item.lastModifiedDateTime
                            ? `Modified: ${new Date(item.lastModifiedDateTime).toLocaleString()}`
                            : ''}
                        </Typography>
                      </Box>
                    }
                  />
                  {item.folder && <ChevronRightIcon color="action" />}
                </ListItemButton>
              </ListItem>
            ))}
            {!loading && filteredDocuments.length === 0 && (
              <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                {emptyMessage}
                {searchQuery && nextLink ? ' Load more to check additional items.' : ''}
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {nextLink && (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Load More'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UnenteredDocumentsSelector;
